<template>
  <Panel header="Danh sách văn bản">
    <div class="w-full">
      <DataTable
        :value="listDocumentStatus"
        :paginator="true"
        stripedRows
        :rows="10"
        :rowsPerPageOptions="[10, 25, 50]"
        :rowHover="true"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        v-model:filters="filters"
        :globalFilterFields="['receiver', 'content']"
        currentPageReportTemplate="Có tổng số {totalRecords} dữ liêu"
        resizableColumns
        tableclass="h-[60px]"
      >
        <template #header>
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex w-full flex-col gap-3">
              <div class="flex gap-3">
                <Button
                  type="button"
                  icon="pi pi-filter-slash"
                  label="Xoá lọc"
                  outlined
                  @click="clearFilter()"
                />
                <span class="relative flex flex-1">
                  <i
                    class="pi pi-search pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm text-slate-400"
                  />
                  <InputText
                    class="!w-[500px] !pl-10 !pr-3"
                    v-model="filters['global'].value"
                    placeholder="Tìm theo tên hoặc số điện thoại"
                  />
                </span>
              </div>
            </div>
          </div>
        </template>

        <Column
          field="title"
          header="Tiêu đề văn bản"
          sortable
          class="h-[60px]"
        ></Column>
        <Column
          field="sender"
          header="Người gởi"
          class="h-[60px]"
          sortable
        ></Column>
        <Column field="time" header="Thời gian" class="h-[60px]" sortable>
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.time) }}
          </template>
        </Column>
        <Column
          field="replyDeadline"
          header="Hạn trả lời"
          class="h-[60px]"
          sortable
        >
          <template #body="slotProps">
            <span :class="deadlineClass(slotProps.data.replyDeadline, slotProps.data.ratio)">
              {{ formatDeadline(slotProps.data.replyDeadline) }}
            </span>
          </template>
        </Column>
        <Column
          field="ratio"
          header="Tỷ lệ xử lý (Đã xử lý/Tổng số)"
          class="h-[60px]"
          sortable
        >
        </Column>
        <Column field="id" header="Chi tiết văn bản" class="h-[60px]" sortable>
          <template #body="slotProps">
            <Button
              outlined
              label="Xem"
              icon="pi pi-file"
              @click="loadDocumentContentDetail(slotProps.data.id)"
            />
          </template>
        </Column>
        <Column field="id" header="Chi tiết xử lý" class="h-[60px]" sortable>
          <template #body="slotProps">
            <Button
              outlined
              label="Xem"
              icon="pi pi-eye"
              :disabled="isEmptyRatio(slotProps.data.ratio)"
              @click="loadDocumentDetail(slotProps.data.id)"
            />
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Confirm Delete Modal -->
  </Panel>
  <div class="contents">
    <div class="w-full" v-if="showDocumentDetail">
      <Dialog
        header="Chi tiết văn bản"
        v-model:visible="showDocumentDetail"
        modal
        :style="{ width: 'min(820px, calc(100vw - 32px))' }"
        contentClass="!p-0"
      >
        <div class="space-y-4 p-5">
          <div class="rounded-md border border-slate-200 bg-slate-50 p-4">
            <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px_160px_190px]">
              <div class="min-w-0">
                <div class="mb-1 text-xs font-semibold uppercase text-slate-500">
                  Tiêu đề văn bản
                </div>
                <div class="break-words text-base font-semibold leading-6 text-slate-900">
                  {{ documentDetail.title || "--" }}
                </div>
              </div>
              <div class="min-w-0">
                <div class="mb-1 text-xs font-semibold uppercase text-slate-500">
                  Người gửi
                </div>
                <div class="truncate text-sm text-slate-700">
                  {{ documentDetail.sender || "--" }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-xs font-semibold uppercase text-slate-500">
                  Thời gian
                </div>
                <div class="text-sm text-slate-700">
                  {{ formatDateTime(documentDetail.time) || "--" }}
                </div>
              </div>
              <div>
                <div class="mb-1 text-xs font-semibold uppercase text-slate-500">
                  Hạn trả lời
                </div>
                <div :class="deadlineClass(documentDetail.replyDeadline)">
                  {{ formatDeadline(documentDetail.replyDeadline) }}
                </div>
              </div>
            </div>
          </div>

          <div class="rounded-md border border-slate-200 bg-white p-4">
            <div class="mb-2 text-xs font-semibold uppercase text-slate-500">
              Nội dung
            </div>
            <div class="whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">
              {{ documentDetail.content || "--" }}
            </div>
          </div>

          <div class="rounded-md border border-slate-200 bg-white">
            <div class="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-900">
              Tập tin đính kèm
            </div>
            <div
              v-if="documentDetail.listAttachments?.length"
              class="divide-y divide-slate-100"
            >
              <button
                v-for="attachment in documentDetail.listAttachments"
                :key="attachment.id"
                type="button"
                class="flex w-full items-center gap-3 px-4 py-3 text-left !text-slate-800 transition hover:bg-teal-50"
                @click="openAttachment(attachment)"
              >
                <span
                  class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-700"
                >
                  <i class="pi pi-paperclip"></i>
                </span>
                <span class="min-w-0 flex-1 truncate font-semibold">
                  {{ attachment.fileName }}
                </span>
                <i class="pi pi-external-link text-xs text-slate-400"></i>
              </button>
            </div>
            <div v-else class="px-4 py-6 text-center text-sm font-medium text-slate-500">
              Không có tập tin đính kèm
            </div>
          </div>
        </div>
      </Dialog>
    </div>
    <div class="w-full" v-if="show">
      <Dialog
        header="Chi tiết trạng thái xử lý văn bản"
        v-model:visible="show"
        modal
        :style="{ width: 'min(920px, calc(100vw - 32px))' }"
        contentClass="!p-0"
      >
        <div class="space-y-4 p-5">
          <div class="rounded-md border border-slate-200 bg-slate-50 p-4">
            <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px_160px_190px]">
              <div class="min-w-0">
                <div
                  class="mb-1 text-xs font-semibold uppercase text-slate-500"
                >
                  Tiêu đề văn bản
                </div>
                <div
                  class="break-words text-base font-semibold leading-6 text-slate-900"
                >
                  {{ documenStatus.title || "--" }}
                </div>
              </div>
              <div class="min-w-0">
                <div
                  class="mb-1 text-xs font-semibold uppercase text-slate-500"
                >
                  Người gửi
                </div>
                <div class="truncate text-sm text-slate-700">
                  {{ documenStatus.sender || "--" }}
                </div>
              </div>
              <div>
                <div
                  class="mb-1 text-xs font-semibold uppercase text-slate-500"
                >
                  Thời gian
                </div>
                <div class="text-sm text-slate-700">
                  {{ formatDateTime(documenStatus.time) || "--" }}
                </div>
              </div>
              <div>
                <div
                  class="mb-1 text-xs font-semibold uppercase text-slate-500"
                >
                  Hạn trả lời
                </div>
                <div :class="deadlineClass(documenStatus.replyDeadline, getStatusRatio())">
                  {{ formatDeadline(documenStatus.replyDeadline) }}
                </div>
              </div>
            </div>
          </div>
          <DataTable
            :value="documenStatus.listReceivers || []"
            :paginator="true"
            stripedRows
            :rows="10"
            :rowsPerPageOptions="[10, 25, 50]"
            :rowHover="true"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            v-model:filters="filters"
            :globalFilterFields="['receiver', 'content']"
            currentPageReportTemplate="Có tổng số {totalRecords} dữ liệu"
            resizableColumns
            tableclass="h-[60px]"
            class="overflow-hidden rounded-md border border-slate-200"
          >
            <!--        <template #header>-->
            <!--          <div class="flex flex-wrap items-center justify-between gap-3">-->
            <!--            <div class="flex w-full flex-col gap-3">-->
            <!--              <div class="flex gap-3">-->
            <!--                <Button-->
            <!--                    type="button"-->
            <!--                    icon="pi pi-filter-slash"-->
            <!--                    label="Xoá lọc"-->
            <!--                    outlined-->
            <!--                    @click="clearFilter()"-->
            <!--                />-->
            <!--                <span class="relative flex flex-1">-->
            <!--                  <i-->
            <!--                      class="pi pi-search pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm text-slate-400"-->
            <!--                  />-->
            <!--                  <InputText-->
            <!--                      class="!w-[500px] !pl-10 !pr-3"-->
            <!--                      v-model="filters['global'].value"-->
            <!--                      placeholder="Tìm theo tên hoặc nội dung"-->
            <!--                  />-->
            <!--                </span>-->
            <!--              </div>-->
            <!--            </div>-->
            <!--          </div>-->
            <!--        </template>-->
            <Column
              field="receiver"
              header="Người nhận"
              sortable
              class="h-[60px]"
            ></Column>
            <Column
              field="content"
              header="Nội dung trả lời"
              class="h-[60px]"
              sortable
            ></Column>
            <Column
              field="status"
              header="Trạng thái"
              class="h-[60px]"
              sortable
            >
              <template #body="slotProps">
                <span
                  v-if="slotProps.data.status"
                  class="inline-flex min-w-[108px] items-center justify-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700"
                >
                  <i class="pi pi-check text-xs"></i>
                  Đã xử lý
                </span>
                <span
                  v-else
                  class="inline-flex min-w-[108px] items-center justify-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700"
                >
                  <i class="pi pi-hourglass text-xs"></i>
                  Chưa xử lý
                </span>
              </template>
            </Column>
            <Column
              field="createTime"
              header="Thời gian nhận"
              class="h-[60px]"
              sortable
            >
              <template #body="slotProps">
                {{ formatDateTime(slotProps.data.createTime) }}
              </template>
            </Column>
            <Column
              field="updateTime"
              header="Thời gian trả lời"
              class="h-[60px]"
              sortable
            >
              <template #body="slotProps">
                {{ formatDateTime(slotProps.data.updateTime) }}
              </template>
            </Column>
          </DataTable>
        </div>
      </Dialog>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ref } from "vue";
import { useToast } from "@/ui/services/toast";
import { FilterMatchMode } from "@/ui/services/api";
import moment from "moment";
import zaloRepository from "@/services/ZaloRepository";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal.vue";
import { DocumentStatus } from "@/models/documentStatus";
import { DocumentStatusDetail } from "@/models/documentStatusDetail";
import { DocumentAdminDetail } from "@/models/documentAdminDetail";

export default {
  components: {
    ConfirmDeleteModal,
  },
  setup() {
    const toast = useToast();
    const listDocumentStatus = ref([] as DocumentStatus[]);
    const documenStatus = ref({} as DocumentStatusDetail);
    const documentDetail = ref({} as DocumentAdminDetail);
    const loadingBar = ref(false);
    const show = ref(false);
    const showDocumentDetail = ref(false);
    // if(store.state.permission == "admin")
    //
    const loadListDocument = () => {
      loadingBar.value = true;
      zaloRepository
        .getListDocumentStatus()
        .then((response) => {
          listDocumentStatus.value = response.data;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Lỗi",
            detail: err.response?.data || "Lỗi khi tải danh sách văn bản",
            life: 2000,
          });
        })
        .finally(() => {
          loadingBar.value = false;
        });
    };

    loadListDocument();

    const loadDocumentDetail = (id: number) => {
      show.value = true;
      zaloRepository
        .getDocumentStatusDetail(id)
        .then((response) => {
          documenStatus.value = response.data;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Lỗi",
            detail: "Lỗi khi tải danh sách văn bản",
            life: 2000,
          });
        })
        .finally(() => {});
    };

    const loadDocumentContentDetail = (id: number) => {
      documentDetail.value = {} as DocumentAdminDetail;
      showDocumentDetail.value = true;
      zaloRepository
        .getDocumentDetailByAdmin(id)
        .then((response) => {
          documentDetail.value = response.data;
        })
        .catch((err) => {
          showDocumentDetail.value = false;
          toast.add({
            severity: "error",
            summary: "Lỗi",
            detail: "Lỗi khi tải chi tiết văn bản",
            life: 2000,
          });
        });
    };

    const filters = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const clearFilter = () => {
      initFilters();
    };

    const initFilters = () => {
      filters.value = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      };
    };
    const formatDateTime = (date) => {
      if (date == null) {
        return "";
      } else {
        return moment(String(date)).format("DD/MM/YYYY");
      }
    };
    const formatDeadline = (date) => {
      if (!date) {
        return "Chưa đặt hạn";
      }
      return moment(String(date)).format("HH:mm DD/MM/YYYY");
    };
    const isRatioCompleted = (ratio) => {
      const [done, total] = String(ratio || "")
        .split("/")
        .map((value) => Number(value));
      return total > 0 && done >= total;
    };
    const deadlineClass = (date, ratio = "") => {
      if (!date) {
        return "inline-flex rounded-md bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600";
      }
      if (!isRatioCompleted(ratio) && moment(String(date)).isBefore(moment())) {
        return "inline-flex rounded-md bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700";
      }
      return "inline-flex rounded-md bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700";
    };
    const getStatusRatio = () => {
      const receivers = documenStatus.value.listReceivers || [];
      return `${receivers.filter((item) => item.status).length}/${receivers.length}`;
    };
    const isEmptyRatio = (ratio) => String(ratio || "").trim() === "0/0";
    const openAttachment = async (attachment: any) => {
      try {
        const response = await zaloRepository.getDocumentFile(attachment.id);
        const fileUrl = URL.createObjectURL(response.data);
        window.open(fileUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
      } catch (err: any) {
        toast.add({
          severity: "error",
          summary: "Lỗi",
          detail: err.response?.data || "Không mở được tài liệu",
          life: 2500,
        });
      }
    };

    return {
      listDocumentStatus,
      filters,
      clearFilter,
      initFilters,
      formatDateTime,
      formatDeadline,
      deadlineClass,
      getStatusRatio,
      isEmptyRatio,
      loadDocumentDetail,
      loadDocumentContentDetail,
      show,
      showDocumentDetail,
      documenStatus,
      documentDetail,
      openAttachment,
    };
  },
};
</script>
`r`n
